import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button'
import { RadioGroup, RadioItem } from '../../components/input/radio.tsx'
import { SearchableCombobox } from '../../components/input/searchable-combobox.tsx'
import { SelectBox } from '../../components/input/select-box.tsx'
import { TextInput } from '../../components/input/text-input.tsx'
import { SCMIcon } from '../../components/third-party-branding/scm-icon.tsx'
import {
  useGetAllAzureOrganizationPages,
  useGetAllAzureProjectPages,
  useGetAllAzureRepositoryPages,
  useGetBitbucketRepositories,
  useGetBitbucketWorkspaces,
  useGetGitLabProjects,
} from '../../utils/api-client/api-hooks.ts'
import type { AddRepositoryRequest } from '../../utils/api-client/api-schemas'
import type { EnvironmentName } from '../../utils/hooks/get-environment-data.ts'
import * as styles from './add-repository-form.css'

export type ConnectionType = 'GitHubApp' | 'GenericGit' | 'Integration'
export type IntegrationType = 'azureDevOps' | 'gitlab' | 'bitbucket'

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

type AddRepositoryFormValues = {
  selectedConnectionType: ConnectionType
  selectedIntegration: IntegrationType
  url: string
  username: string
  password: string
  selectedAzureOrganizationId: string | undefined
  selectedAzureProjectId: string | undefined
  selectedAzureRepositoryId: string | undefined
  gitLabProjectSearchQuery: string
  selectedBitbucketWorkspaceSlug: string | undefined
  selectedBitbucketRepositorySlug: string | undefined
}

const FORM_DEFAULT_VALUES: AddRepositoryFormValues = {
  selectedConnectionType: 'GitHubApp',
  selectedIntegration: 'azureDevOps',
  url: '',
  username: '',
  password: '',
  selectedAzureOrganizationId: undefined,
  selectedAzureProjectId: undefined,
  selectedAzureRepositoryId: undefined,
  gitLabProjectSearchQuery: '',
  selectedBitbucketWorkspaceSlug: undefined,
  selectedBitbucketRepositorySlug: undefined,
}

export const useAddRepositoryForm = (defaultValueOverrides: Partial<AddRepositoryFormValues>) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

export type AddRepositoryFormProps = {
  environmentName: EnvironmentName
  onClose: () => unknown
  githubAppName: string | undefined
  isAzureIntegrationEnabled: boolean
  isGitlabIntegrationEnabled: boolean
  isBitbucketIntegrationEnabled: boolean
  addRepository: (params: AddRepositoryRequest, options?: { onSuccess?: () => unknown }) => unknown
}

export const AddRepositoryForm: React.FC<AddRepositoryFormProps> = ({
  environmentName,
  onClose,
  githubAppName,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useStore(form.store, s => s.values.selectedConnectionType)

  return (
    <form className={styles.form}>
      <ConnectionTypeRadioInput
        environmentName={environmentName}
        isGitHubIntegrationEnabled={!!githubAppName}
        isAzureIntegrationEnabled={isAzureIntegrationEnabled}
        isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
        isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
      />
      {match(selectedConnectionType)
        .with('GitHubApp', () =>
          githubAppName ? <PixeebotAppConnector onClose={onClose} githubAppName={githubAppName} /> : null
        )
        .with('GenericGit', () => (
          <>
            <div className={styles.inputGroup}>
              <GenericGitFields />
            </div>
            <div className={styles.buttonGroup}>
              <GhostButton onClick={onClose}>Cancel</GhostButton>
              <GenericGitConnectButton addRepository={addRepository} />
            </div>
          </>
        ))
        .with('Integration', () =>
          isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled ? (
            <IntegrationConnector
              onClose={onClose}
              isAzureIntegrationEnabled={isAzureIntegrationEnabled}
              isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
              isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
              addRepository={addRepository}
            />
          ) : null
        )
        .exhaustive()}
    </form>
  )
}

type ConnectionTypeRadioInputProps = Pick<
  AddRepositoryFormProps,
  'environmentName' | 'isAzureIntegrationEnabled' | 'isGitlabIntegrationEnabled' | 'isBitbucketIntegrationEnabled'
> & {
  isGitHubIntegrationEnabled: boolean
}

const ConnectionTypeRadioInput: React.FC<ConnectionTypeRadioInputProps> = ({
  environmentName,
  isGitHubIntegrationEnabled,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useStore(form.store, s => s.values.selectedConnectionType)

  return (
    <RadioGroup
      value={selectedConnectionType}
      onValueChange={type => form.setFieldValue('selectedConnectionType', type as ConnectionType)}
      className={styles.radioGroup}
    >
      {isGitHubIntegrationEnabled && (
        <RadioItem
          value="GitHubApp"
          id="github-app"
          label={`Install the ${environmentName === 'ntt' ? 'Github' : 'Pixeebot GitHub'} application (GitHub repositories only)`}
        />
      )}
      <RadioItem value="GenericGit" id="generic-git" label="Connect via Git client (clone a repository from any SCM)" />
      {(isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled) && (
        <RadioItem value="Integration" id="integration" label="Connect to repositories from an existing integration" />
      )}
    </RadioGroup>
  )
}

type PixeebotAppConnectorProps = Pick<AddRepositoryFormProps, 'onClose' | 'githubAppName'>

const PixeebotAppConnector: React.FC<PixeebotAppConnectorProps> = ({ onClose, githubAppName }) => {
  return (
    <div className={styles.buttonGroup}>
      <GhostButton onClick={onClose}>Cancel</GhostButton>
      <DefaultButton
        href={`https://github.com/apps/${githubAppName}/installations/new`}
        buttonType="primary"
        onClick={onClose}
      >
        Go to GitHub
      </DefaultButton>
    </div>
  )
}

type IntegrationConnectorProps = Pick<
  AddRepositoryFormProps,
  | 'onClose'
  | 'isAzureIntegrationEnabled'
  | 'isGitlabIntegrationEnabled'
  | 'isBitbucketIntegrationEnabled'
  | 'addRepository'
>

const IntegrationConnector: React.FC<IntegrationConnectorProps> = ({
  onClose,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedIntegration = useStore(form.store, s => s.values.selectedIntegration)

  const integrationOptions = useMemo(() => {
    function* generateOptions() {
      if (isAzureIntegrationEnabled) {
        yield {
          value: 'azureDevOps' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="azure" size="medium" />
              Azure DevOps
            </span>
          ),
        }
      }
      if (isGitlabIntegrationEnabled) {
        yield {
          value: 'gitlab' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="gitlab" size="medium" />
              GitLab
            </span>
          ),
        }
      }
      if (isBitbucketIntegrationEnabled) {
        yield {
          value: 'bitbucket' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="bitbucket" size="medium" />
              Bitbucket
            </span>
          ),
        }
      }
    }
    return [...generateOptions()]
  }, [isAzureIntegrationEnabled, isGitlabIntegrationEnabled, isBitbucketIntegrationEnabled])

  return (
    <>
      <div className={styles.inputGroup} aria-label="Integration selections">
        <SelectBox
          label="Integration"
          value={selectedIntegration}
          onValueChange={value => {
            form.setFieldValue('selectedIntegration', value as IntegrationType)
          }}
          options={integrationOptions}
        />
        {match(selectedIntegration)
          .with('azureDevOps', () => (isAzureIntegrationEnabled ? <AzureDevOpsFields /> : null))
          .with('gitlab', () => (isGitlabIntegrationEnabled ? <GitLabSearchableCombobox /> : null))
          .with('bitbucket', () => (isBitbucketIntegrationEnabled ? <BitbucketFields /> : null))
          .exhaustive()}
      </div>
      <div className={styles.buttonGroup}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        {match(selectedIntegration)
          .with('azureDevOps', () =>
            isAzureIntegrationEnabled ? <AzureDevOpsConnectButton addRepository={addRepository} /> : null
          )
          .with('gitlab', () =>
            isGitlabIntegrationEnabled ? <GitLabConnectButton addRepository={addRepository} /> : null
          )
          .with('bitbucket', () =>
            isBitbucketIntegrationEnabled ? <BitbucketConnectButton addRepository={addRepository} /> : null
          )
          .exhaustive()}
      </div>
    </>
  )
}

const AzureDevOpsFields: React.FC = () => {
  const form = useFormContext()
  const selectedAzureOrganizationId = useStore(form.store, s => s.values.selectedAzureOrganizationId)
  const selectedAzureProjectId = useStore(form.store, s => s.values.selectedAzureProjectId)
  const selectedAzureRepositoryId = useStore(form.store, s => s.values.selectedAzureRepositoryId)

  const azureOrganizationResults = useGetAllAzureOrganizationPages({ enabled: true })
  const azureProjectResults = useGetAllAzureProjectPages({ organizationId: selectedAzureOrganizationId })
  const azureRepositoryResults = useGetAllAzureRepositoryPages({
    organizationName: selectedAzureOrganizationId ?? '',
    projectId: selectedAzureProjectId ?? '',
    enabled: !!selectedAzureOrganizationId && !!selectedAzureProjectId,
  })

  const azureOrganizations = azureOrganizationResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureProjects = azureProjectResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureRepositories = azureRepositoryResults.flatMap(r => r.data?._embedded?.items ?? [])

  const azureOrganizationOptions = azureOrganizations.map(org => ({
    value: org.name,
    label: org.name,
  }))
  const azureProjectOptions = azureProjects.map(project => ({
    value: project.id,
    label: project.name,
  }))
  const azureRepositoryOptions = azureRepositories.map(repo => ({
    value: repo.id,
    label: repo.name,
    disabled: repo.pixee_id !== undefined,
  }))

  return (
    <>
      <SelectBox
        label="Organization"
        value={selectedAzureOrganizationId}
        onValueChange={value => {
          form.setFieldValue('selectedAzureOrganizationId', value)
          form.setFieldValue('selectedAzureProjectId', '')
          form.setFieldValue('selectedAzureRepositoryId', '')
        }}
        placeholder="Select organization"
        options={azureOrganizationOptions}
      />
      <SelectBox
        label="Project"
        value={selectedAzureProjectId}
        onValueChange={value => {
          form.setFieldValue('selectedAzureProjectId', value)
          form.setFieldValue('selectedAzureRepositoryId', '')
        }}
        disabled={!selectedAzureOrganizationId}
        placeholder="Select project"
        options={azureProjectOptions}
      />
      <SelectBox
        label="Available repositories"
        value={selectedAzureRepositoryId}
        onValueChange={value => form.setFieldValue('selectedAzureRepositoryId', value)}
        disabled={!selectedAzureProjectId}
        placeholder="Select repository"
        options={azureRepositoryOptions}
      />
    </>
  )
}

type ConnectButtonProps = Pick<AddRepositoryFormProps, 'addRepository'>

const AzureDevOpsConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const selectedAzureOrganizationId = useStore(form.store, s => s.values.selectedAzureOrganizationId)
  const selectedAzureProjectId = useStore(form.store, s => s.values.selectedAzureProjectId)
  const selectedAzureRepositoryId = useStore(form.store, s => s.values.selectedAzureRepositoryId)

  const azureProjectResults = useGetAllAzureProjectPages({ organizationId: selectedAzureOrganizationId })
  const azureRepositoryResults = useGetAllAzureRepositoryPages({
    organizationName: selectedAzureOrganizationId ?? '',
    projectId: selectedAzureProjectId ?? '',
    enabled: !!selectedAzureOrganizationId && !!selectedAzureProjectId,
  })

  const azureProjects = azureProjectResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureRepositories = azureRepositoryResults.flatMap(r => r.data?._embedded?.items ?? [])

  const selectedAzureRepository = azureRepositories.find(repo => repo.id === selectedAzureRepositoryId)
  const selectedAzureProject = azureProjects.find(proj => proj.id === selectedAzureProjectId)

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedAzureRepositoryId ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedAzureOrganizationId && selectedAzureProject && selectedAzureRepository) {
          addRepository({
            type: 'azure',
            organization: selectedAzureOrganizationId,
            project: selectedAzureProject.name,
            name: selectedAzureRepository.name,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const BitbucketFields: React.FC = () => {
  const form = useFormContext()
  const selectedBitbucketWorkspaceSlug = useStore(form.store, s => s.values.selectedBitbucketWorkspaceSlug)
  const selectedBitbucketRepositorySlug = useStore(form.store, s => s.values.selectedBitbucketRepositorySlug)

  const { data: bitbucketWorkspaces } = useGetBitbucketWorkspaces({ enabled: true })
  const { data: bitbucketRepositories } = useGetBitbucketRepositories({
    workspaceSlug: selectedBitbucketWorkspaceSlug!,
    enabled: !!selectedBitbucketWorkspaceSlug,
  })

  const bitbucketWorkspaceOptions = (bitbucketWorkspaces?._embedded?.items ?? []).map(workspace => ({
    value: workspace.slug,
    label: workspace.name,
  }))

  const bitbucketRepositoryOptions = (bitbucketRepositories?._embedded?.items ?? []).map(repo => ({
    value: repo.slug,
    label: repo.name,
    disabled: repo.pixee_id !== undefined,
  }))

  return (
    <>
      <SelectBox
        label="Workspace"
        value={selectedBitbucketWorkspaceSlug}
        onValueChange={value => {
          form.setFieldValue('selectedBitbucketWorkspaceSlug', value)
          form.setFieldValue('selectedBitbucketRepositorySlug', undefined)
        }}
        placeholder="Select workspace"
        options={bitbucketWorkspaceOptions}
      />
      <SelectBox
        label="Repository"
        value={selectedBitbucketRepositorySlug}
        onValueChange={value => form.setFieldValue('selectedBitbucketRepositorySlug', value)}
        disabled={!selectedBitbucketWorkspaceSlug}
        placeholder="Select repository"
        options={bitbucketRepositoryOptions}
      />
    </>
  )
}

const BitbucketConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const selectedBitbucketWorkspaceSlug = useStore(form.store, s => s.values.selectedBitbucketWorkspaceSlug)
  const selectedBitbucketRepositorySlug = useStore(form.store, s => s.values.selectedBitbucketRepositorySlug)

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedBitbucketRepositorySlug ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedBitbucketWorkspaceSlug && selectedBitbucketRepositorySlug) {
          addRepository({
            type: 'bitbucket',
            workspace: selectedBitbucketWorkspaceSlug,
            slug: selectedBitbucketRepositorySlug,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const GitLabConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const gitLabProjectSearchQuery = useStore(form.store, s => s.values.gitLabProjectSearchQuery)

  const { data: gitlabProjectsData } = useGetGitLabProjects({
    search: gitLabProjectSearchQuery || undefined,
    enabled: true,
  })
  const gitlabProjects = gitlabProjectsData?._embedded?.items ?? []
  const selectedGitLabProject = gitlabProjects.find(project => project.name_with_namespace === gitLabProjectSearchQuery)

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedGitLabProject ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedGitLabProject) {
          addRepository({
            type: 'gitlab',
            name: selectedGitLabProject.path,
            namespace: selectedGitLabProject.namespace_full_path,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const GitLabSearchableCombobox: React.FC = () => {
  const form = useFormContext()
  const gitLabProjectSearchQuery = useStore(form.store, s => s.values.gitLabProjectSearchQuery)

  const { data: gitlabProjectsData, isFetching: isGitlabProjectsFetching } = useGetGitLabProjects({
    search: gitLabProjectSearchQuery || undefined,
    enabled: true,
  })
  const gitlabProjects = gitlabProjectsData?._embedded?.items ?? []

  const gitlabProjectOptions = useMemo(
    () =>
      gitlabProjects.map(project => ({
        path: project.path,
        value: project.name_with_namespace,
        label: project.name_with_namespace,
        enabled: project.pixee_id === undefined,
        checked: project.pixee_id !== undefined,
      })),
    [gitlabProjects]
  )

  return (
    <SearchableCombobox
      label="Available projects"
      placeholder="Search GitLab projects..."
      searchValue={gitLabProjectSearchQuery}
      setSearchValue={value => form.setFieldValue('gitLabProjectSearchQuery', value)}
      options={gitlabProjectOptions}
      isFetching={isGitlabProjectsFetching}
      side="bottom"
      avoidCollisions={false}
    />
  )
}

const GenericGitFields: React.FC = () => {
  const form = useFormContext()
  const url = useStore(form.store, s => s.values.url)
  const username = useStore(form.store, s => s.values.username)
  const password = useStore(form.store, s => s.values.password)

  return (
    <>
      <TextInput
        label="URL"
        id="repository-url"
        name="url"
        placeholder="ex: https://scm.com/org/repo-lang.git"
        value={url}
        onChange={value => form.setFieldValue('url', value)}
        isRequired
      />
      <TextInput
        label="Username"
        id="repository-username"
        name="username"
        placeholder="Enter username"
        value={username}
        onChange={value => form.setFieldValue('username', value)}
        explicitLabelCondition="optional"
      />
      <TextInput
        type="password"
        label="Password"
        id="repository-password"
        name="password"
        placeholder="Enter password"
        value={password}
        onChange={value => form.setFieldValue('password', value)}
        explicitLabelCondition="optional"
      />
    </>
  )
}

const GenericGitConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const url = useStore(form.store, s => s.values.url)
  const username = useStore(form.store, s => s.values.username)
  const password = useStore(form.store, s => s.values.password)

  const extractRepoName = (repoUrl: string): string => {
    const match = repoUrl.match(/\/([^/]+)\.git$/)
    return match ? (match[1] ?? repoUrl) : repoUrl
  }

  return (
    <DefaultButton
      buttonType="primary"
      state={url ? 'default' : 'disabled'}
      onClick={() =>
        addRepository(
          {
            type: 'git',
            name: extractRepoName(url),
            credentials: { username, password },
            url,
          },
          {
            onSuccess: () => {
              form.setFieldValue('url', '')
              form.setFieldValue('username', '')
              form.setFieldValue('password', '')
            },
          }
        )
      }
    >
      Connect
    </DefaultButton>
  )
}
